import { useEffect, useState } from "react";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { LanguageSwitcher } from "@airhelp/react";
import { ListItem } from "@airhelp/react";
import {
  currencies,
  locales,
  Locale,
  mapLocaleToFlagKey,
  Currency,
} from "../../../app-settings/localesAndCurrencies";
import { useTranslation } from "react-i18next";
import { useAppSettings } from "../../../app-settings/AppSettingsContext";
import { CountryCode } from "@airhelp/flags";
import FlagBox from "v2/components/funnel/ui-elements/language-switcher/FlagBox";

type LocaleItem = {
  code: string;
  displayName: string;
  icon: CountryCode;
};

const mapLocaleItemsToListItems = (items: LocaleItem[]): ListItem[] => {
  return items.map(({ code, displayName, icon }) => ({
    id: code,
    name: displayName,
    icon,
  }));
};

const suggestedLocales: LocaleItem[] = [locales.en, locales["pt-BR"]];
const suggestedLocalesSettings: ListItem[] = mapLocaleItemsToListItems(suggestedLocales);
const currenciesSettings: ListItem[] = [
  {
    id: currencies.EUR.code,
    name: currencies.EUR.displayName,
    icon: currencies.EUR.icon,
  },
  {
    id: currencies.USD.code,
    name: currencies.USD.displayName,
    icon: currencies.USD.icon,
  },
];

const wasCurrencySelected = (data: ListItem): boolean => {
  return currenciesSettings.map((currency) => currency.id).includes(data.id);
};

const SettingsModal = ({ showCurrencyList = true }: { showCurrencyList?: boolean }) => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { locale, setLocale, currency, setCurrency } = useAppSettings();
  const [flagKey, setFlagKey] = useState<CountryCode>(mapLocaleToFlagKey(locale));
  const mapLocaleToActiveItem = (locale: Locale): string => {
    return locale.toLowerCase();
  };

  const [activeItems, setActiveItems] = useState<string[]>([
    mapLocaleToActiveItem(locale),
    currency,
  ]);
  const onItemClick = (data: ListItem): void => {
    if (wasCurrencySelected(data)) {
      setCurrency(data.id as Currency);
    } else {
      setLocale(data.id as Locale);
      setFlagKey(mapLocaleToFlagKey(data.id as Locale));
    }
    onClose();
  };

  const supportedLocalesSettings: ListItem[] = mapLocaleItemsToListItems(
    Object.values(locales).filter((l) => suggestedLocales.indexOf(l) === -1),
  );

  const settingsModalContent = [
    {
      listHeading: t("footer.settings_modal.suggested_languages"),
      listItems: suggestedLocalesSettings,
    },
    {
      listHeading: t("footer.settings_modal.select_language"),
      listItems: supportedLocalesSettings,
      listAutoflow: { base: "row", lg: "column" },
    },
  ];

  const settingsModalContentWithCurrency = [
    {
      listHeading: t("footer.settings_modal.suggested_languages"),
      listItems: suggestedLocalesSettings,
    },
    {
      listHeading: t("footer.settings_modal.select_language"),
      listItems: supportedLocalesSettings,
      listAutoflow: { base: "row", lg: "column" },
    },
    {
      listHeading: t("footer.settings_modal.select_currency"),
      listItems: currenciesSettings,
    },
  ];

  useEffect(() => {
    setActiveItems([locale, currency]);
    setFlagKey(mapLocaleToFlagKey(locale));
  }, [locale, currency, flagKey]);

  return (
    <FlagBox flagKey={flagKey} locale={locale} handleClick={onOpen}>
      <Modal
        data-testid="languageSwitcherModal"
        isOpen={isOpen}
        onClose={onClose}
        size="4xl"
        isCentered={false}
      >
        <ModalOverlay />
        <ModalContent
          overflow="hidden"
          data-testid="languageSwitcherModalContent"
          maxW={{ md: "fit-content" }}
          maxHeight="calc(100dvh - var(--chakra-space-))"
        >
          <ModalCloseButton
            data-testid="modal-close-button"
            borderRadius="sm"
            boxSize={2}
            sx={{
              svg: { boxSize: 2.5 },
              _hover: { backgroundColor: "primary.200" },
              _active: { backgroundColor: "primary.800", color: "greyscale.200" },
            }}
          />
          <ModalBody
            pb={0}
            pr={2}
            pt={16}
            mr={{ base: 4, md: 8 }}
            mb={{ base: 4, md: 8 }}
            overflowX="hidden"
          >
            <LanguageSwitcher
              activeItems={activeItems}
              lists={showCurrencyList ? settingsModalContentWithCurrency : settingsModalContent}
              onItemClick={onItemClick}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </FlagBox>
  );
};

export default SettingsModal;
